import React, { useEffect, useState } from 'react'
import { AppLayout, CustomButton, CustomDropdown, SearchBar, Typography, useApi, useNavigate, Grid2, GridColDef } from '../../utils/globalImports'
import CustomInvoiceTable from '../../components/dataTable/CustomInvoiceTable';
import {
  Stack, Box, Chip, Paper, Tooltip, IconButton,
  Divider, ToggleButton, ToggleButtonGroup, Button,
} from '@mui/material';
import { headingStyles } from '../../assets/styles/CommonStyles';
import { InvoiceProject } from '../../interfaces/Invoicing.interfaces';
import {
  VisibilityOutlined, AddCircleOutlineOutlined,
  CalendarMonthOutlined, SearchOutlined,
  FilterListOutlined, ClearOutlined,
  TrendingUpOutlined, TrendingDownOutlined, SwapVertOutlined,
} from '@mui/icons-material';
interface ActiveMonthDetails {
  year: number,
  month: number,
  currentLockDate: Date,
  minAllowedDate: Date,
  maxAllowedDate: Date
}
const allMonths = [
  { value: 1, text: 'January' }, { value: 2, text: 'February' },
  { value: 3, text: 'March' }, { value: 4, text: 'April' },
  { value: 5, text: 'May' }, { value: 6, text: 'June' },
  { value: 7, text: 'July' }, { value: 8, text: 'August' },
  { value: 9, text: 'September' }, { value: 10, text: 'October' },
  { value: 11, text: 'November' }, { value: 12, text: 'December' },
];
const statusChipStyles: Record<string, { bg: string; color: string; border: string }> = {
  Invoiced: { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  Forecasted: { bg: '#fff8e1', color: '#e65100', border: '#ffcc80' },
};

const sortOptions = [
  { value: "projectNo", text: "Project No." },
  { value: "projectName", text: "Project Name" },
]

const ProjectInvoiceList: React.FC = () => {
  const [activeMonthDetails, setActiveMonthDetails] = useState<ActiveMonthDetails | null>(null)
  const [projectData, setProjectData] = useState<InvoiceProject[]>([]);
  const [filteredData, setFilteredData] = useState<InvoiceProject[]>([]);
  const [selectedRows, setSelectedRows] = useState<InvoiceProject[]>([]);

  const [status, setStatus] = useState<number>(-1);
  const [filterQuery, setFilterQuery] = useState<string>('');
  const [changeFilter, setChangeFilter] = useState<ChangeFilter>('all');

  const [toast, setToast] = useState<any>();
  type ChangeFilter = 'all' | 'positive' | 'negative';
  const { execute, loading } = useApi();
  const navigate = useNavigate();

  const currentMonth = activeMonthDetails ? activeMonthDetails?.month : new Date().getMonth() + 1
  const currentYear = activeMonthDetails ? activeMonthDetails?.year : new Date().getFullYear()

  const allYears = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i;
    return { value: year, text: year.toString() };
  });
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [lastFetchedPeriod, setLastFetchedPeriod] = useState({ month: currentMonth, year: currentYear });


  const filteredMonths = React.useMemo(() => {
    const months =
      selectedYear === currentYear
        ? allMonths.slice(0, currentMonth)
        : [...allMonths];
    return months.sort((a, b) => a.value - b.value);
  }, [selectedYear, currentMonth]);

  const isCurrentPeriod = lastFetchedPeriod.month === currentMonth && lastFetchedPeriod.year === currentYear;

  /* ── active filter chips ── */
  const activeFilters: { label: string; onClear: () => void }[] = [];
  if (filterQuery) activeFilters.push({ label: `"${filterQuery}"`, onClear: () => setFilterQuery('') });
  if (status === 0) activeFilters.push({ label: 'Status: Invoiced', onClear: () => setStatus(-1) });
  if (status === 1) activeFilters.push({ label: 'Status: Forecasted', onClear: () => setStatus(-1) });
  if (changeFilter !== 'all') activeFilters.push({ label: `Change: ${changeFilter === 'positive' ? 'Positive' : 'Negative'}`, onClear: () => setChangeFilter('all') });

  /* ── navigation ── */
  const handleNavigate = (projectId?: number) => {
    navigate(`/invoicing/invoices/${projectId}`,
      {
        state: {
          filterMonth: lastFetchedPeriod.month,
          filterYear: lastFetchedPeriod.year
        }
      }
    )
  };
  const handleCreateNavigation = (row: InvoiceProject) => navigate('invoice-type', { state: { selectedRow: [row] } });
  const handleCreateInvoice = () => navigate('invoice-type', { state: { selectedRow: selectedRows } });

  const handleYearChange = (e: any) => {
    const year = Number(e.target.value);

    setSelectedYear(year);
    if (year === currentYear) {
      setSelectedMonth(currentMonth);
    } else {
      setSelectedMonth(1);
    }
  };

  /* ── data ── */
  // const fetchProjectInvoice = async () => {
  //   const payload = { projectManagerId: localStorage.getItem('userId'), month: selectedMonth, year: selectedYear };
  //   const { data } = await execute<InvoiceProject[]>('post', '/Invoice/dashboard', payload);
  //   const result = data || [];
  //   setProjectData(result);
  //   setFilteredData(result);
  // };
  const fetchActiveMonthDetails = async () => {
    try {
      const { data } = await execute<ActiveMonthDetails>(
        "get",
        "BaselineConfig/GetActiveMonthDetails"
      );

      if (!data) {
        setToast({
          open: true,
          message: "No active month found",
          type: "error",
        });
        return;
      }

      setActiveMonthDetails(data);

      setSelectedMonth(data.month);
      setSelectedYear(data.year);

      await fetchProjectInvoice(data.month, data.year);

    } catch (error) {
      setToast({
        open: true,
        message: "Failed to fetch config dates",
        type: "error",
      });
    }
  };
  const fetchProjectInvoice = async (
    month: number,
    year: number
  ) => {
    try {
      const payload = {
        projectManagerId: localStorage.getItem("userId"),
        month,
        year,
      };

      const { data } = await execute<InvoiceProject[]>(
        "post",
        "/Invoice/dashboard",
        payload
      );

      const result = data || [];

      setProjectData(result);
      setFilteredData(result);
      setLastFetchedPeriod({ month, year });

      return result;

    } catch (error) {
      setToast({
        open: true,
        message: "Failed to fetch invoices",
        type: "error",
      });

      return [];
    }
  };
  useEffect(() => {
    fetchActiveMonthDetails()
  }, [])

  const handleFilters = (data: InvoiceProject[]) => {
    const query = filterQuery.toLowerCase();
    const filtered = data.filter((item) => {
      const matchesSearch =
        !query ||
        item.projectName?.toLowerCase().includes(query) ||
        item.projectNo?.toLowerCase().includes(query) ||
        item.clientName?.toLowerCase().includes(query);

      const matchesStatus =
        status === -1 ||
        (status === 0 && item.status === 'Invoiced') ||
        (status === 1 && item.status === 'Forecasted');

      const changeVal = Number(item.change ?? item.forecasted ?? 0);
      const matchesChange =
        changeFilter === 'all' ||
        (changeFilter === 'positive' && changeVal > 0) ||
        (changeFilter === 'negative' && changeVal < 0);

      return matchesSearch && matchesStatus && matchesChange;
    });
    setFilteredData(filtered);
  }
  /* ── filtering ── */
  const handleSearch = async () => {

    if (selectedMonth < 1) {
      setToast({
        open: true,
        message: "Please select a month",
        type: "error",
      });
      return;
    }

    let data = projectData;

    if (
      selectedMonth !== lastFetchedPeriod.month ||
      selectedYear !== lastFetchedPeriod.year
    ) {
      data = await fetchProjectInvoice(
        selectedMonth,
        selectedYear
      );
    }

    handleFilters(data);
  };

  const handleClearFilters = () => {
    setFilterQuery('');
    setStatus(-1);
    setChangeFilter('all');
    setSelectedMonth(currentMonth)
    setSelectedYear(currentYear)
    setLastFetchedPeriod({ month: currentMonth, year: currentYear });
    fetchProjectInvoice(currentMonth, currentYear)
  };

  /* ── columns ── */
  const projectColumns: GridColDef[] = [
    { field: 'projectNo', headerName: 'Project No.', flex: 1 },
    { field: 'projectName', headerName: 'Project Name', flex: 1.2 },
    { field: 'clientName', headerName: 'Client Name', flex: 1.2 },
    { field: 'forecasted', headerName: 'Forecasted', flex: 0.9, filterable: false },
    { field: 'netIntoAccounts', headerName: 'Net Into Accounts', flex: 1, filterable: false },
    {
      field: 'change', headerName: 'Change', flex: 0.8, filterable: false,
      renderCell: (params) => {
        const val = Number(params.value);
        const color = val > 0 ? '#2e7d32' : val < 0 ? '#c62828' : '#555';
        return (
          <Box sx={{ fontWeight: 600, color, fontSize: '13px' }}>
            {val > 0 ? '+' : ''}{params.value}
          </Box>
        );
      },
    },
    {
      field: 'status', headerName: 'Status', flex: 0.9,
      renderCell: (params) => {
        const s = params.value as string;
        const style = statusChipStyles[s] ?? { bg: '#e3f2fd', color: '#1e66ab', border: '#90caf9' };
        return (
          <Chip label={s} size="small" sx={{
            backgroundColor: style.bg, color: style.color,
            border: `1px solid ${style.border}`, fontWeight: 600, fontSize: '11px', height: '24px',
          }} />
        );
      },
    },
    {
      field: 'action', headerName: 'Action', flex: 0.8,
      sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (params) => {
        const isInvoiced = params.row.status === 'Invoiced';
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isInvoiced && (
              <Tooltip title="View Invoices" arrow>
                <IconButton size="medium" onClick={() => handleNavigate(params.row.projectId)}
                  sx={{ color: '#1e66ab', '&:hover': { backgroundColor: '#e3f0fb' } }}>
                  <VisibilityOutlined fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
            {isCurrentPeriod && (
              <Tooltip title="Create Invoice" arrow>
                <IconButton size="medium" onClick={() => handleCreateNavigation(params.row)}
                  sx={{ color: '#96c121', '&:hover': { backgroundColor: '#f0f7e0' } }}>
                  <AddCircleOutlineOutlined fontSize="medium" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ];

  /* ── shared toggle button style ── */
  const toggleBtnSx = {
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'none',
    px: 1.5,
    py: 0.6,
    borderRadius: '8px !important',
    border: '1px solid #dde6f4 !important',
    color: '#555',
    '&.Mui-selected': { backgroundColor: '#1e66ab', color: 'white', borderColor: '#1e66ab !important' },
    '&.Mui-selected:hover': { backgroundColor: '#155b9a' },
    '&:hover': { backgroundColor: '#f0f4ff' },
  };

  return (
    <AppLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

        {/* ── Page Header ── */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={headingStyles}>Invoices</Typography>
        </Box>

        {/* ── Filter Panel ── */}
        <Paper elevation={0} sx={{ border: '1px solid #e0eaf6', borderRadius: '14px', overflow: 'hidden', flexShrink: 0 }}>

          {/* Panel header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, backgroundColor: '#f0f6ff', borderBottom: '1px solid #e0eaf6' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListOutlined sx={{ fontSize: 18, color: '#1e66ab' }} />
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e66ab', fontFamily: 'Poppins' }}>
                Filters
              </Typography>
              {activeFilters.length > 0 && (
                <Chip
                  label={`${activeFilters.length} active`}
                  size="small"
                  sx={{ height: '20px', fontSize: '10px', fontWeight: 700, backgroundColor: '#1e66ab', color: 'white' }}
                />
              )}
            </Box>
            {activeFilters.length > 0 && (
              <Button
                size="small"
                startIcon={<ClearOutlined sx={{ fontSize: 14 }} />}
                onClick={handleClearFilters}
                sx={{ fontSize: '12px', color: '#c62828', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#fce4ec' }, borderRadius: '8px', px: 1.5 }}
              >
                Clear All
              </Button>
            )}
          </Box>

          <Box sx={{ padding: '12px 20px 28px 20px', backgroundColor: '#f7faff' }}>
            <Grid2 container spacing={2} alignItems="flex-end">

              {/* ── Section label: Period ── */}
              <Grid2 size={{ xs: 12 }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Poppins', mb: -1 }}>
                  Period
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <CustomDropdown
                  label="Select Year"
                  value={selectedYear}
                  onChange={handleYearChange}
                  options={allYears}
                  sx={{ backgroundColor: 'white', borderRadius: '8px' }}
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <CustomDropdown
                  label="Select Month"
                  value={selectedMonth}
                  disableSorting={true}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  options={filteredMonths}
                  sx={{ backgroundColor: 'white', borderRadius: '8px' }}
                />
              </Grid2>

              {/* ── Divider ── */}
              <Grid2 size={{ xs: 12 }}>
                <Divider sx={{ borderColor: '#e0eaf6' }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'Poppins', mt: 1.5, mb: -1 }}>
                  Search & Filter
                </Typography>
              </Grid2>

              {/* Search */}
              <Grid2 size={{ xs: 12, sm: 8, md: 5 }}>
                <SearchBar

                  placeholder="Search by Project Name, Number, Client"
                  onQueryChange={(val: string) => setFilterQuery(val)}
                  sx={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: 'none', border: '1px solid #dde6f4' }}
                  onKeyDown={(e: any) => { if (e.key === 'Enter') handleSearch(); }}
                />


              </Grid2>

              {/* Status */}
              <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                <CustomDropdown
                  value={status}
                  onChange={(e) => setStatus(Number(e.target.value))}
                  label="Select Status"
                  sx={{ backgroundColor: 'white', borderRadius: '8px' }}
                  options={[

                    { value: 0, text: 'Invoiced' },
                    { value: 1, text: 'Forecasted' },
                  ]}
                />
              </Grid2>



              {/* Search & Create Invoice Buttons */}
              <Grid2 size={{ xs: 12, sm: 'auto' }} sx={{ ml: 'auto' }}>
                <Stack direction="row" spacing={1}>
                  <CustomButton
                    capsule
                    sx={{ textTransform: 'none', height: '40px', px: 3, whiteSpace: 'nowrap' }}
                    onClick={handleSearch}
                    backgroundColor="#1e66ab"
                    hoverColor="rgba(150, 193, 33, 0.75)"
                  >
                    <SearchOutlined sx={{ fontSize: 18, mr: 0.5 }} />
                    Search
                  </CustomButton>

                  <CustomButton
                    capsule
                    sx={{ textTransform: 'none', height: '40px', px: 3, whiteSpace: 'nowrap' }}
                    onClick={handleCreateInvoice}
                    backgroundColor="#96c121"
                    hoverColor="rgba(30, 102, 171, 0.85)"
                    disabled={!selectedRows.length || !isCurrentPeriod}
                  >
                    <AddCircleOutlineOutlined sx={{ fontSize: 18, mr: 0.5 }} />
                    Create Invoice
                  </CustomButton>
                </Stack>
              </Grid2>
            </Grid2>
          </Box>
        </Paper>

        {/* ── Active Filter Chips ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minHeight: '28px', margin: '8px 0 8px 0', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '13px', color: '#555', fontFamily: 'Poppins' }}>
            {filteredData.length} project{filteredData.length !== 1 ? 's' : ''} found
          </Typography>

          {selectedRows.length > 0 && (
            <Chip
              label={`${selectedRows.length} selected`}
              size="small"
              sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', fontWeight: 600, fontSize: '11px' }}
            />
          )}

          {activeFilters.map((f, i) => (
            <Chip
              key={i}
              label={f.label}
              size="small"
              onDelete={f.onClear}
              sx={{ backgroundColor: '#e3f0fb', color: '#1e66ab', border: '1px solid #90c4f5', fontWeight: 600, fontSize: '11px' }}
            />
          ))}
        </Box>

        <Box sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* ── Data Table ── */}
          <CustomInvoiceTable
            sortOptions={sortOptions}
            rows={filteredData}
            columns={projectColumns}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            checkBox={true}
            loading={loading}
            gridHeight={320}
            onRowClick={(id) => handleNavigate(id)}
          />
        </Box>
      </Box>
    </AppLayout>
  );
};

export default ProjectInvoiceList;
